import { BASE_URL } from "../config/config.js";

// Delete user by ID
const deleteUser = async (userId) => {
  try {
    const response = await fetch(`${BASE_URL}/users/${userId}`, {
      method: "DELETE",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    });
    if (response.status === 204) {
      // 204 No Content: deletion successful, no body
      return { success: true };
    } else if (response.status === 200) {
      return await response.json();
    } else {
      throw new Error("Failed to delete user");
    }
  } catch (error) {
    console.error("Error:", error);
    return { success: false, error };
  }
};

// Get all users
const getUsers = async () => {
  try {
    const response = await fetch(`${BASE_URL}/users`, {
      method: "GET",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    });
    if (response.status === 200) {
      return await response.json();
    } else {
      throw new Error("Failed to fetch users");
    }
  } catch (error) {
    console.error("Error:", error);
    return [];
  }
};

// Get all roles
const getRoles = async () => {
  try {
    const response = await fetch(`${BASE_URL}/roles`, {
      method: "GET",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    });
    if (response.status === 200) {
      return await response.json();
    } else {
      throw new Error("Failed to fetch roles");
    }
  } catch (error) {
    console.error("Error:", error);
    return [];
  }
};

// Update user fields
const updateUser = async (id, userData) => {
  try {
    const body = JSON.stringify(userData);
    const response = await fetch(`${BASE_URL}/users/${id}`, {
      method: "PUT",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body,
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to update user (${response.status}): ${errorText}`);
    }

    const responseText = await response.text();
    if (!responseText) {
      return {};
    }

    try {
      return JSON.parse(responseText);
    } catch {
      return {};
    }
  } catch (error) {
    console.error("Error:", error);
    return null;
  }
};

const UserDetails = async (fastVisa_userid) => {
  try {
    const response = await fetch(`${BASE_URL}/users/${fastVisa_userid}`, {
      method: "GET",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    });

    if (response.status === 200) {
      return await response.json();
    } else {
      throw new Error("Failed to fetch user data");
    }
  } catch (error) {
    console.error("Error:", error);
  }
};

const ApplicantSearch = async (fastVisa_userid) => {
  const requestBody = { fastVisa_userid };
  try {
    const response = await fetch(`${BASE_URL}/applicants/search`, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify(requestBody),
    });

    if (response.status === 200) {
      return await response.json();
    } else {
      throw new Error("Failed to fetch applicant data");
    }
  } catch (error) {
    console.error("Error:", error);
  }
};

const ApplicantDetails = async (applicant_userid) => {
  try {
    const response = await fetch(`${BASE_URL}/applicants/${applicant_userid}`, {
      method: "GET",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    });

    if (response.status === 200) {
      return await response.json();
    } else {
      throw new Error("Failed to fetch applicant data");
    }
  } catch (error) {
    console.error("Error:", error);
  }
};

const ApplicantUpdate = async (applicant_userid, requestBody) => {
  try {
    const response = await fetch(`${BASE_URL}/applicants/${applicant_userid}`, {
      method: "PUT",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify(requestBody),
    });

    if (response.status === 200) {
      return await response.json();
    } else {
      throw new Error("Failed to update applicant data");
    }
  } catch (error) {
    console.error("Error:", error);
  }
};

const ApplicantDelete = async (applicant_userid) => {
  try {
    const response = await fetch(`${BASE_URL}/applicants/${applicant_userid}`, {
      method: "DELETE",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    });

    if (response.status === 204) {
      // 204 No Content: deletion successful, no body
      return { success: true };
    } else if (response.status === 200) {
      return await response.json();
    } else {
      throw new Error("Failed to delete applicant");
    }
  } catch (error) {
    console.error("Error:", error);
    return { success: false, error };
  }
};

const StartApplicantContainer = async (applicant_userid) => {
  const requestBody = { applicant_id: applicant_userid };
  try {
    const response = await fetch(`${BASE_URL}/applicants/start`, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify(requestBody),
    });

    if (response.status === 200) {
      return await response.json();
    } else {
      throw new Error("Failed to start container");
    }
  } catch (error) {
    console.error("Error:", error);
    throw error;
  }
};

const StopApplicantContainer = async (applicant_userid) => {
  const requestBody = { applicant_id: applicant_userid };
  try {
    const response = await fetch(`${BASE_URL}/applicants/stop`, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify(requestBody),
    });

    if (response.status === 200) {
      return await response.json();
    } else {
      throw new Error("Failed to stop container");
    }
  } catch (error) {
    console.error("Error:", error);
    throw error;
  }
};

const GetApplicantPassword = async (applicant_userid) => {
  try {
    const response = await fetch(
      `${BASE_URL}/applicants/${applicant_userid}/password`,
      {
        method: "GET",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      },
    );

    if (response.status === 200) {
      return await response.json();
    } else {
      throw new Error("Failed to fetch applicant password");
    }
  } catch (error) {
    console.error("Error:", error);
  }
};

// Get PayPal config from API Gateway endpoint
const getPayPalConfig = async () => {
  try {
    const response = await fetch(`${BASE_URL}/paypal-config`, {
      method: "GET",
      headers: {
        Accept: "application/json",
      },
    });
    if (response.status === 200) {
      return await response.json();
    } else {
      throw new Error("Failed to fetch PayPal config");
    }
  } catch (error) {
    console.error("Error fetching PayPal config:", error);
    return null;
  }
};

// Get current USD to MXN exchange rate from open.er-api.com (no access key required)
const getUSDMXNExchangeRate = async () => {
  try {
    const response = await fetch("https://open.er-api.com/v6/latest/USD", {
      method: "GET",
      headers: {
        Accept: "application/json",
      },
    });
    if (response.status === 200) {
      const data = await response.json();
      // data.rates.MXN contains the exchange rate
      return data.rates.MXN;
    } else {
      throw new Error("Failed to fetch exchange rate");
    }
  } catch (error) {
    console.error("Error fetching exchange rate:", error);
    return null;
  }
};
// Authenticate AIS credentials and get list of applicants.
// Request: { username, password } (optionally country_code if backend accepts).
// Success (200): { applicants: [...], username, country_code }; each applicant has
// schedule_id, applicant_name, passport, ds_160_id, consul_appointment_date, asc_appointment_date.
// Failure (non-2xx): returns { applicants: null, error: 'invalid_credentials' } for UI message.
const authenticateAIS = async ({ username, password, country_code }) => {
  try {
    const body = country_code
      ? { username, password, country_code }
      : { username, password };
    const response = await fetch(`${BASE_URL}/ais/auth`, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify(body),
    });
    if (response.status === 200) {
      const data = await response.json();
      return data;
    } else {
      return { applicants: null, error: "invalid_credentials" };
    }
  } catch (error) {
    console.error("AIS Auth Error:", error);
    return { applicants: null, error: "invalid_credentials" };
  }
};

// Notify admin about AIS authentication failure
const notifyAdminAISFailure = async ({
  username,
  ais_username,
  country_code,
}) => {
  try {
    const response = await fetch(`${BASE_URL}/admin/notify-ais-failure`, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ username, ais_username, country_code }),
    });
    if (response.status === 200) {
      return await response.json();
    } else {
      console.error("Failed to notify admin about AIS failure");
    }
  } catch (error) {
    console.error("Error notifying admin:", error);
  }
};

// Change user password
const changePassword = async ({
  userId,
  username,
  currentPassword,
  newPassword,
}) => {
  try {
    const response = await fetch(`${BASE_URL}/auth/change-password`, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        userId,
        username,
        currentPassword,
        newPassword,
      }),
    });

    if (response.status === 200) {
      const data = await response.json();
      return { success: true, message: data.message };
    } else if (response.status === 401) {
      const errorData = await response.json();
      return {
        success: false,
        message: errorData.message || "Current password is incorrect",
      };
    } else {
      const errorData = await response.json();
      return {
        success: false,
        message: errorData.message || "Failed to change password",
      };
    }
  } catch (error) {
    console.error("Error changing password:", error);
    return {
      success: false,
      message: "An error occurred while changing password",
    };
  }
};

// Create a new user
const createUser = async (userData) => {
  try {
    const response = await fetch(`${BASE_URL}/users`, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify(userData),
    });

    if (response.status === 201) {
      return { success: true, status: 201 };
    } else {
      let errorMessage = "Registration failed";
      try {
        const errorData = await response.json();
        errorMessage =
          errorData.message || errorData.error || "Registration failed";
      } catch {
        errorMessage = response.statusText || "Registration failed";
      }
      return { success: false, error: errorMessage };
    }
  } catch (error) {
    console.error("Error creating user:", error);
    return {
      success: false,
      error: error.message || "An unexpected error occurred",
    };
  }
};

// Search for user by username
const searchUserByUsername = async (username) => {
  try {
    const response = await fetch(`${BASE_URL}/users/search`, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ username }),
    });

    if (response.status === 200) {
      return await response.json();
    } else {
      throw new Error("Failed to search for user");
    }
  } catch (error) {
    console.error("Error searching user:", error);
    return null;
  }
};

// Create a new applicant
const createApplicant = async (applicantData) => {
  try {
    console.log(
      "[APIFunctions] createApplicant - Sending request to:",
      `${BASE_URL}/applicants`,
    );
    console.log("[APIFunctions] createApplicant - Payload:", applicantData);

    const response = await fetch(`${BASE_URL}/applicants`, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify(applicantData),
    });

    console.log(
      "[APIFunctions] createApplicant - Response status:",
      response.status,
    );

    // Accept both 200 and 201 as success
    if (response.status === 200 || response.status === 201) {
      const data = await response.json();
      console.log("[APIFunctions] createApplicant - Success, data:", data);
      return data;
    } else {
      // Try to get error details from response
      let errorMessage = "Failed to create applicant";
      try {
        const errorData = await response.json();
        console.error(
          "[APIFunctions] createApplicant - Error response:",
          errorData,
        );
        errorMessage = errorData.message || errorData.error || errorMessage;
      } catch {
        console.error(
          "[APIFunctions] createApplicant - Could not parse error response",
        );
      }
      throw new Error(errorMessage);
    }
  } catch (error) {
    console.error("[APIFunctions] createApplicant - Exception:", error);
    return null;
  }
};

// Login user
const loginUser = async (username, password) => {
  try {
    const response = await fetch(`${BASE_URL}/auth/login`, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ username, password }),
    });

    if (response.status === 200) {
      return { success: true };
    } else if (response.status === 403) {
      const errorData = await response.json();
      return {
        success: false,
        status: 403,
        error: errorData.message || "Email not verified",
        errorCode: errorData.error_code,
      };
    } else {
      throw new Error("Failed to log in");
    }
  } catch (error) {
    console.error("Error logging in:", error);
    return { success: false, error: error.message };
  }
};

// Get user permissions
const getUserPermissions = async (userId) => {
  try {
    const response = await fetch(`${BASE_URL}/users/permissions/${userId}`, {
      method: "GET",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    });

    if (response.status === 200) {
      return await response.json();
    } else {
      throw new Error("Failed to fetch user permissions");
    }
  } catch (error) {
    console.error("Error fetching permissions:", error);
    return null;
  }
};

// Verify email with token
const verifyEmail = async (token) => {
  try {
    const response = await fetch(`${BASE_URL}/users/verify-email`, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ token }),
    });

    if (response.status === 200) {
      return { success: true, data: await response.json() };
    } else {
      const errorData = await response.json();
      return {
        success: false,
        error: errorData.message || "Verification failed",
        errorCode: errorData.error_code,
        status: response.status,
      };
    }
  } catch (error) {
    console.error("Error verifying email:", error);
    return { success: false, error: error.message };
  }
};

// Send a generic email via the FastVisaSendEmail Lambda
const sendEmail = async ({ recipient, subject, message, html_body, cc, bcc }) => {
  try {
    const body = { recipient, subject, message };
    if (html_body) body.html_body = html_body;
    if (cc) body.cc = cc;
    if (bcc) body.bcc = bcc;

    const response = await fetch(`${BASE_URL}/admin/send-email`, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify(body),
    });

    if (response.status === 200) {
      return { success: true, data: await response.json() };
    } else {
      const errorData = await response.json().catch(() => ({}));
      return { success: false, error: errorData.message || "Failed to send email" };
    }
  } catch (error) {
    console.error("Error sending email:", error);
    return { success: false, error: error.message };
  }
};

// Resend verification email
const resendVerificationEmail = async (email) => {
  try {
    const response = await fetch(`${BASE_URL}/users/resend-verification`, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ email }),
    });

    if (response.status === 200) {
      return { success: true, data: await response.json() };
    } else {
      const errorData = await response.json();
      return {
        success: false,
        error: errorData.message || "Failed to resend email",
      };
    }
  } catch (error) {
    console.error("Error resending verification email:", error);
    return { success: false, error: error.message };
  }
};

// Send forgot-password request — POST /auth/forgot-password
const forgotPassword = async (email, language = 'en') => {
  try {
    const response = await fetch(`${BASE_URL}/auth/forgot-password`, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ email, language }),
    });
    const data = await response.json();
    return { success: response.status === 200, status: response.status, ...data };
  } catch (error) {
    console.error("Error sending forgot password request:", error);
    return { success: false, error: error.message };
  }
};

// Validate reset token — GET /auth/validate-reset-token?token=...
const validateResetToken = async (token) => {
  try {
    const response = await fetch(
      `${BASE_URL}/auth/validate-reset-token?token=${encodeURIComponent(token)}`,
      {
        method: "GET",
        headers: {
          Accept: "application/json",
        },
      }
    );
    const data = await response.json();
    return { success: response.status === 200, status: response.status, ...data };
  } catch (error) {
    console.error("Error validating reset token:", error);
    return { success: false, error: error.message };
  }
};

// Update notification settings — PATCH /users/:id/notification-settings
const updateNotificationSettings = async (userId, frequencyMinutes) => {
  try {
    const response = await fetch(`${BASE_URL}/users/${userId}/notification-settings`, {
      method: "PATCH",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ notification_frequency_minutes: frequencyMinutes }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(
        `Failed to update notification settings (${response.status}): ${errorText}`
      );
    }

    const responseText = await response.text();
    if (!responseText) return {};

    try {
      return JSON.parse(responseText);
    } catch {
      return {};
    }
  } catch (error) {
    console.error("Error updating notification settings:", error);
    throw error;
  }
};

// Reset password — POST /auth/reset-password
const resetPassword = async (token, newPassword, language = 'en') => {
  try {
    const response = await fetch(`${BASE_URL}/auth/reset-password`, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ token, newPassword, language }),
    });
    const data = await response.json();
    return { success: response.status === 200, status: response.status, ...data };
  } catch (error) {
    console.error("Error resetting password:", error);
    return { success: false, error: error.message };
  }
};

export {
  UserDetails,
  ApplicantSearch,
  ApplicantDetails,
  ApplicantUpdate,
  StartApplicantContainer,
  StopApplicantContainer,
  ApplicantDelete,
  GetApplicantPassword,
  getUsers,
  updateUser,
  getRoles,
  getPayPalConfig,
  deleteUser,
  getUSDMXNExchangeRate,
  authenticateAIS,
  notifyAdminAISFailure,
  changePassword,
  createUser,
  searchUserByUsername,
  createApplicant,
  loginUser,
  getUserPermissions,
  verifyEmail,
  resendVerificationEmail,
  forgotPassword,
  validateResetToken,
  resetPassword,
  sendEmail,
  updateNotificationSettings,
};
